import { useEffect, useMemo, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useTranslation } from "react-i18next";

import { IShopItem, IUserBalance } from "../../types";
import ShopCard from "../ShopCard";
import ShopItemModal from "../ShopItemModal";

import styles from "./ShopItems.module.scss";
import CustomEmptyState from "@/components/emptyState/CustomEmptyState";
import SearchIcon from "@/assets/icons/SearchIcon";
import { useIsPromoMini } from "@/hooks/useIsPromoMini";

const SHOP_ITEM_CARD_WIDTH = 172;
const SHOP_ITEMS_DESKTOP_GRID_GAP = 16;
const SHOP_ITEMS_MOBILE_GRID_GAP = 12;
const SHOP_ITEMS_INITIAL_SKELETON_ROWS = 2;
const SHOP_ITEMS_FETCH_MORE_SKELETON_ROWS = 1;

type Props = {
  items: IShopItem[];
  userBalanceData: IUserBalance[];
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isShopItemsLoading: boolean;
  isFetchingNextPage: boolean;
};

const ShopItems = ({
  items,
  userBalanceData,
  hasNextPage,
  fetchNextPage,
  isShopItemsLoading,
  isFetchingNextPage,
}: Props) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [item, setItem] = useState<IShopItem | null>(null);
  const [gridColumns, setGridColumns] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const isMini = useIsPromoMini();

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const calculateColumns = () => {
      const gap = window.innerWidth <= 768 ? SHOP_ITEMS_MOBILE_GRID_GAP : SHOP_ITEMS_DESKTOP_GRID_GAP;
      const columns = Math.max(
        1,
        Math.floor((container.clientWidth + gap) / (SHOP_ITEM_CARD_WIDTH + gap))
      );

      setGridColumns(columns);
    };

    calculateColumns();

    const resizeObserver = new ResizeObserver(calculateColumns);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [isShopItemsLoading]);

  const handleButtonClick = (item: IShopItem) => {
    setItem(item);
    setIsPopupOpen(true);
  };

  const handleFetchNextPage = () => {
    if (!hasNextPage || isFetchingNextPage) return;

    fetchNextPage();
  };

  const { t } = useTranslation();

  const fallbackGridColumns = isMini ? 2 : 5;
  const skeletonGridColumns = gridColumns || fallbackGridColumns;

  const initialSkeletonCount = skeletonGridColumns * SHOP_ITEMS_INITIAL_SKELETON_ROWS;

  const fetchMoreSkeletonCount = useMemo(() => {
    const missingCurrentRowItems =
      items.length % skeletonGridColumns === 0
        ? 0
        : skeletonGridColumns - (items.length % skeletonGridColumns);

    return missingCurrentRowItems + skeletonGridColumns * SHOP_ITEMS_FETCH_MORE_SKELETON_ROWS;
  }, [items.length, skeletonGridColumns]);

  const renderSkeletonItems = (count: number, className = "") => (
    <div
      className={`${styles.itemsGrid} ${isMini ? "" : styles.itemsGridDefault} ${className}`}
      aria-hidden="true"
    >
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className={styles.itemCell}>
          <div className={styles.skeletonCard}>
            <div className={styles.skeletonImage} />
            <div className={styles.skeletonContent}>
              <div className={styles.skeletonCategory} />
              <div className={styles.skeletonTitle} />
              <div className={styles.skeletonButton} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderSkeletonCells = (count: number) =>
    Array.from({ length: count }).map((_, index) => (
      <div key={`skeleton-${index}`} className={styles.itemCell} aria-hidden="true">
        <div className={styles.skeletonCard}>
          <div className={styles.skeletonImage} />
          <div className={styles.skeletonContent}>
            <div className={styles.skeletonCategory} />
            <div className={styles.skeletonTitle} />
            <div className={styles.skeletonButton} />
          </div>
        </div>
      </div>
    ));

  if (isShopItemsLoading) {
    return (
      <div ref={containerRef} className={`${styles.container} ${isMini ? styles.miniContainer : ""}`}>
        {renderSkeletonItems(initialSkeletonCount)}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <CustomEmptyState
        className={styles.emptyState}
        img={<SearchIcon />}
        title={t("shop.emptyTitle")}
        text={t("shop.emptyDesc")}
      />
    );
  }

  return (
    <div ref={containerRef} className={`${styles.container} ${isMini ? styles.miniContainer : ""}`}>
      <InfiniteScroll
        dataLength={items.length}
        next={handleFetchNextPage}
        hasMore={hasNextPage}
        hasChildren={items.length > 0}
        loader={null}
        className={styles.infiniteScroll}
      >
        <div className={`${styles.itemsGrid} ${isMini ? "" : styles.itemsGridDefault}`}>
          {items.map((item) => (
            <div key={item.id} className={styles.itemCell}>
              <ShopCard
                item={item}
                userBalanceData={userBalanceData}
                handleBuyButtonClick={handleButtonClick}
              />
            </div>
          ))}

          {isFetchingNextPage && renderSkeletonCells(fetchMoreSkeletonCount)}
        </div>
      </InfiniteScroll>

      <ShopItemModal
        isPopupOpen={isPopupOpen}
        setIsPopupOpen={setIsPopupOpen}
        item={item}
        userBalanceData={userBalanceData}
      />
    </div>
  );
};

export default ShopItems;
